import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { environment } from '../../../environments/environment';

const mensagens: Record<number, string> = {
  400: 'Requisição inválida',
  401: 'Não autorizado',
  403: 'Acesso negado',
  404: 'Recurso não encontrado',
  409: 'Conflito de dados',
  422: 'Dados inválidos',
  500: 'Erro interno do servidor',
  502: 'Erro de gateway',
  503: 'Serviço indisponível',
  504: 'Timeout do gateway',
};

function parseBackendErrorBody(body: unknown): unknown {
  if (typeof body !== 'string') {
    return body;
  }

  const trimmed = body.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return body;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return body;
  }
}

function extractBackendMessage(error: HttpErrorResponse): string | null {
  if (error.error instanceof ErrorEvent) {
    return null;
  }

  const backendError = parseBackendErrorBody(error.error);

  if (typeof backendError === 'string' && backendError.trim()) {
    return backendError.trim();
  }

  if (!backendError || typeof backendError !== 'object') {
    return null;
  }

  const payload = backendError as {
    detail?: string | Array<{ msg?: string }> | { msg?: string; message?: string };
    message?: string;
  };

  if (typeof payload.detail === 'string') {
    return payload.detail;
  }
  if (payload.message) {
    return payload.message;
  }
  if (Array.isArray(payload.detail)) {
    const msgs = payload.detail
      .map((d) => d.msg ?? '')
      .filter((msg) => !!msg);
    return msgs.length ? msgs.join(', ') : null;
  }
  if (payload.detail && typeof payload.detail === 'object') {
    return payload.detail.msg || payload.detail.message || null;
  }

  return null;
}

function resolveProductionMessage(error: HttpErrorResponse): string {
  const backendMessage = extractBackendMessage(error);
  if (backendMessage) {
    return backendMessage;
  }

  if (error.status === 422) {
    return mensagens[422];
  }

  return mensagens[error.status] ?? 'Ocorreu um erro inesperado na requisição';
}

function resolveDevelopmentMessage(error: HttpErrorResponse): string {
  if (error.error instanceof ErrorEvent) {
    return `Erro: ${error.error.message}`;
  }

  const backendMessage = extractBackendMessage(error);
  if (backendMessage) {
    return backendMessage;
  }

  return mensagens[error.status] ?? 'Ocorreu um erro inesperado na requisição';
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const authRoutes = ['/autenticacao/login', '/autenticacao/callback', '/autenticacao/refresh'];
      if (authRoutes.some((route) => req.url.includes(route))) {
        return throwError(() => error);
      }

      const errorMessage = environment.production
        ? resolveProductionMessage(error)
        : resolveDevelopmentMessage(error);

      notificationService.error(errorMessage);
      return throwError(() => error);
    }),
  );
};
